import React, { useCallback, useMemo, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, Link, navigate } from '@reach/router'
import { AppBar, InputAdornment, Grid, Typography, IconButton } from '@material-ui/core'
import { Menu } from '@refera/ui-web'
import { ArrowDown1, User } from '@refera/ui-icons'
import SearchIcon from '@material-ui/icons/Search'

import classnames from 'classnames'

import { getProvidersListFiltered } from '_modules/provider/actions'
import { getAgenciesFiltered } from '_modules/agency/actions'
import { getArticles } from '_modules/utils/actions'
import { userSelector } from '_/modules/authentication/selectors'

import Svg from '_components/svg'
import SearchBar from '_components/search-bar'
import Button from '_components/button'

import useHandleInput from '_hooks/use-handle-input'
import useRolePermission from '_hooks/use-role-permission'

import { ROUTES } from '_utils/constants'

import AddPersonIcon from '_assets/icons/ic-add-person.svg'
import AddAgencyIcon from '_assets/icons/ic-house.svg'
import IconLogo from '_assets/icons/ic-refera-re.svg'
import IconFullLogo from '_assets/svgs/refera-logo.svg'

import Notifications from '../notifications'
import Support from '../support'

import HeaderItems from './header-items'
import ModuleSelector from './module-selector'
import useStyles from './styles'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'

export const HEADER_THEME = {
  DEFAULT: 'default',
  NO_BUTTONS: 'noButtons',
}

const Header = ({ className, theme }) => {
  const styles = useStyles()
  const dispatch = useDispatch()
  const { checkIfUserDoesNotPermission } = useRolePermission()

  const location = useLocation()
  const { pathname } = useLocation()

  const user = useSelector(userSelector)
  const { isAdmin, isIntermediary, isSAAS } = useRolePermission()

  const [input, handleInput] = useHandleInput()
  const [searchValue, setSearchValue] = useState('')
  const [anchorEl, setAnchorEl] = useState(null)

  const handleSearch = useCallback(() => {
    setSearchValue(input)
  }, [input])

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Enter') {
        handleSearch()
      }
    },
    [handleSearch]
  )
  const [openTooltip, setOpenTooltip] = useState(false)
  const isLandingPage = location.pathname === ROUTES.LANDING

  const agencyIdFromUrl = useMemo(() => {
    const match = pathname.match(/\/imobiliaria\/(\d+)/)
    return match ? parseInt(match[1], 10) : null
  }, [pathname])

  const handleNavigation = useCallback(route => {
    handleClose()
    navigate(route)
  }, [])

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleClick = event => {
    setAnchorEl(event.currentTarget)
    setOpenTooltip(false)
  }

  const inputProps = useMemo(
    () => ({
      endAdornment: (
        <InputAdornment position="end">
          <SearchIcon onClick={handleSearch} style={{ cursor: 'pointer' }} />
        </InputAdornment>
      ),
    }),
    [handleSearch]
  )

  useEffect(() => {
    dispatch(getArticles()).catch(() => {})
  }, [dispatch])

  useEffect(() => {
    if (pathname.includes('prestadores')) {
      dispatch(getProvidersListFiltered({ name: searchValue, mainTradesman: true }))
    } else if (pathname.includes('/imobiliarias') || pathname.includes('/gestao-imobiliarias')) {
      dispatch(getAgenciesFiltered({ name: searchValue }))
    } else if (pathname.includes('/imobiliaria')) {
      if (isIntermediary) {
        if (agencyIdFromUrl !== user.agency) {
          navigate('/nao-encontrado')
        }
      }
    }
  }, [dispatch, pathname, searchValue, agencyIdFromUrl, user])

  const handleLinkManageProviders = useCallback(() => {
    if (location.pathname === ROUTES.MANAGE_PROVIDERS) {
      if (
        checkIfUserDoesNotPermission(PERMISSIONS.TRADESMAN_CONFIGS, PERMISSIONS_ACTIONS.ADD) ||
        isAdmin
      ) {
        return null
      }
      return navigate(ROUTES.CREATE_PROVIDER)
    }

    if (checkIfUserDoesNotPermission(PERMISSIONS.INTERMEDIARY_CONFIGS, PERMISSIONS_ACTIONS.ADD)) {
      return null
    }

    return navigate(ROUTES.CREATE_AGENCY)
  }, [location.pathname, isAdmin])

  const renderButtonTradesman = useMemo(
    () =>
      !isAdmin && (
        <Button className={styles.add} onClick={handleLinkManageProviders}>
          <Svg icon={AddPersonIcon} className={styles.iconButton} />
          Novo prestador
        </Button>
      ),
    [location.pathname, styles.add, styles.iconButton, isAdmin]
  )

  return (
    <AppBar
      elevation={0}
      position="fixed"
      className={classnames(styles.header, styles[theme], className)}
    >
      <Grid className={styles.leftWrapper}>
        {theme !== HEADER_THEME.NO_BUTTONS ? (
          <Grid className={styles.logoContainer}>
            <Link to="/">
              <Svg className={styles.logo} icon={IconLogo} />
            </Link>
          </Grid>
        ) : (
          <Link to="/">
            <Svg className={styles.fullLogo} icon={IconFullLogo} />
          </Link>
        )}
        {(isAdmin || isSAAS) && <ModuleSelector className={styles.ModuleSelector} />}
      </Grid>

      {theme !== HEADER_THEME.NO_BUTTONS && (
        <Grid className={styles.container}>
          <Grid className={styles.rightContent}>
            {location.pathname === ROUTES.MANAGE_PROVIDERS && (
              <Grid className={styles.content}>
                <SearchBar
                  className={styles.searchBar}
                  value={input}
                  onChange={handleInput}
                  placeholder="Pesquisar prestador"
                  InputProps={inputProps}
                  onKeyDown={handleKeyDown}
                />
                {renderButtonTradesman}
              </Grid>
            )}

            {location.pathname === ROUTES.MANAGE_AGENCIES && (
              <Grid className={styles.content}>
                <SearchBar
                  className={styles.searchBar}
                  value={input}
                  onChange={handleInput}
                  placeholder="Pesquisar intermediária"
                  InputProps={inputProps}
                  onKeyDown={handleKeyDown}
                />
                <Button className={styles.add} onClick={handleLinkManageProviders}>
                  <Svg icon={AddAgencyIcon} className={styles.iconButton} />
                  Nova intermediária
                </Button>
              </Grid>
            )}

            {!isLandingPage && (
              <>
                <Notifications />
                <Support />
              </>
            )}

            <Grid className={styles.userCircle1} disabled>
              <User className={styles.userIcon1} />
            </Grid>
            <Typography className={styles.userLabel1}>{user?.name}</Typography>
            <IconButton
              aria-haspopup="true"
              aria-controls={openTooltip && 'basic-menu'}
              aria-expanded={openTooltip && 'true'}
              onClick={handleClick}
            >
              <ArrowDown1 color="white" />
            </IconButton>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              MenuListProps={{ 'aria-labelledby': 'basic-button', className: styles.menuList }}
              PaperProps={{ className: styles.paper }}
            >
              <HeaderItems handleNavigation={handleNavigation} handleClose={handleClose} />
            </Menu>
          </Grid>
        </Grid>
      )}
    </AppBar>
  )
}

export default React.memo(Header)
